import type { PrintSheetHtml, PrintSheetLayout } from '../print/templates';

/**
 * The Vendor Credit (piece 22, Payment, allocations, vendor credits and
 * refunds). 🔨 It is its own entity and never a borrowed `Payment`: a
 * borrowed `paymentId` would break both `amountPaid` sums. It applies
 * through `PaymentAllocation` with `sourceType` VendorCredit, and its
 * PartiallyApplied and Applied states derive from that sum.
 */
export interface VendorCreditFacts {
  /** The credit's own amount, integer cents. */
  amountCents: number;
  /** The open allocations sourced from it, integer cents. */
  appliedCents: number;
  /** The stored member: Draft, Open or Void. */
  stored: string;
}

/** 🔨 A derived member is never written to the column; it shadows the stored value at read time. */
export function vendorCreditStatus(facts: VendorCreditFacts): string {
  if (facts.stored === 'Draft' || facts.stored === 'Void') return facts.stored;
  if (facts.appliedCents <= 0) return 'Open';
  return facts.appliedCents >= facts.amountCents ? 'Applied' : 'PartiallyApplied';
}

/**
 * 🔒 Whether the credit's page offers Apply credit (decision 7580): an Open or
 * Partially Applied credit with an amount left. A Draft has not posted, a Void
 * never will and an Applied credit has nothing left to give.
 */
export function vendorCreditMayApply(facts: { status: string; remainingCents: number }): boolean {
  return (facts.status === 'Open' || facts.status === 'PartiallyApplied') && facts.remainingCents > 0;
}

/** One application of a credit to a Bill, as the credit's page lists it (decision 7580). */
export interface VendorCreditApplicationRow {
  id: string;
  billId: string;
  billNumber: string | null;
  amountCents: number;
  appliedOn: string;
  /** Null while the application stands; the moment it was dissociated. */
  closedAt: string | null;
}

/**
 * 🔒 The Vendor Credit as its page reads it (decision 7580), declared once so the
 * service that builds it and the screen that reads it are the same shape. A
 * credit carries no table of lines (decision 0449), so the answer carries none.
 */
export interface VendorCreditPageAnswer {
  id: string;
  creditNumber: string;
  vendorCompanyId: string;
  vendorName: string;
  creditDate: string;
  currencyCode: string;
  amountCents: number;
  appliedCents: number;
  remainingCents: number;
  reason: string;
  status: string;
  approvalStatus: string;
  sourceBillId: string | null;
  sourceBillNumber: string | null;
  /** What the credit was applied to, oldest first, a dissociated application included and marked. */
  applications: VendorCreditApplicationRow[];
  /** Whether the page offers Apply credit; the document write gate still decides for the viewer. */
  mayApply: boolean;
  /** 🔒 The layout the sheet prints on and the sheet itself, as the printer's own HTML (decision 0567; T169). */
  printLayout?: PrintSheetLayout | null;
  printSheet?: PrintSheetHtml | null;
  /** 🔒 Why this viewer writes nothing on the document, or null where they may (decision 0687). */
  writeRefusal?: string | null;
}

/** The Bill's own paid state, the same shape on the other side of the ledger. */
export function billStatus(facts: { amountCents: number; amountPaidCents: number; stored: string }): string {
  if (facts.stored === 'Draft' || facts.stored === 'Void') return facts.stored;
  if (facts.amountPaidCents <= 0) return 'Received';
  return facts.amountPaidCents >= facts.amountCents ? 'Paid' : 'PartiallyPaid';
}

export function vendorCreditProblem(input: { vendorCompanyId?: string | null; creditDate?: string | null; amountCents?: number | null; reason?: string | null }): string | null {
  if (!input.vendorCompanyId) return 'a vendor credit names the vendor that granted it';
  if (!input.creditDate || !/^\d{4}-\d{2}-\d{2}$/.test(input.creditDate)) return 'a vendor credit carries its date as a calendar date; pick the date the vendor granted it';
  if (!input.amountCents || !Number.isInteger(input.amountCents) || input.amountCents <= 0) return 'a vendor credit carries an amount above zero, in whole cents; enter the amount the vendor credited';
  if (!input.reason || !input.reason.trim()) return 'a vendor credit states why the vendor granted it';
  return null;
}

/** A credit against a Bill's own vendor alone: a credit from one vendor never settles another's bill. */
export function vendorCreditBillProblem(creditVendorId: string, billVendorId: string): string | null {
  return creditVendorId === billVendorId ? null : 'this credit is a different vendor’s; a credit settles only the bills of the vendor that granted it';
}

export function vendorCreditApprovalProblem(status: string, approvalStatus: string): string | null {
  if (status === 'Void') return 'a void vendor credit is not approved';
  if (approvalStatus === 'Pending') return 'the approval gate holds this vendor credit; it posts once it is approved';
  if (approvalStatus === 'Rejected') return 'this vendor credit was rejected; edit it and resubmit before it posts';
  if (status !== 'Draft') return 'this vendor credit is already open and posted';
  return null;
}

// ---- Applying a credit to a Bill (piece 27, Vendor credit application) ----

/**
 * 🔨 Application is manual only: a person explicitly applies an available
 * credit to a specific Bill, and no path applies one to the next eligible Bill
 * on its own (piece 27). A credit is a negotiating position as often as it is
 * an accounting fact, and applying it is a decision somebody makes.
 *
 * Each application is its own `PaymentAllocation` row with `sourceType`
 * VendorCredit — one credit against three Bills is three rows — because an
 * `appliedToBillId` column on the credit cannot express partial application
 * across several Bills.
 */
export interface CreditApplicationFacts {
  /** The credit's own amount, integer cents. */
  creditAmountCents: number;
  /** What the credit's open allocations already carry, integer cents. */
  creditAppliedCents: number;
  /** The credit's stored member: a Draft or Void credit applies to nothing. */
  creditStatus: string;
  /** The credit's vendor and the Bill's vendor; a credit settles only its own vendor's bills. */
  creditVendorCompanyId: string;
  billVendorCompanyId: string;
  /** What the Bill still owes, integer cents. */
  billOpenBalanceCents: number;
  /** The Bill's stored member: a Draft or Void bill takes no credit. */
  billStatus: string;
  /** What the person asked to apply, integer cents. */
  amountCents: number;
}

/** What is left of a credit to apply: its amount less what its open allocations carry, never below zero. */
export function remainingCreditCents(facts: { creditAmountCents: number; creditAppliedCents: number }): number {
  return Math.max(0, facts.creditAmountCents - facts.creditAppliedCents);
}

/**
 * 🔒 The Bill statuses a credit applies on (piece 24, Vendor credits; T740): a
 * posted bill that still owes. A Draft has not posted, a Void never will, and a
 * Paid bill owes nothing, so the banner on those offers View credits and no Apply.
 */
export const BILL_STATUSES_TAKING_CREDIT: readonly string[] = ['Received', 'PartiallyPaid'];

/** Whether a bill in this status takes a credit, which is when its banner offers Apply credit. */
export function billTakesCredit(status: string): boolean {
  return BILL_STATUSES_TAKING_CREDIT.includes(status);
}

/**
 * 🔒 Why a bill in this status takes no credit, in a sentence naming the status,
 * or null where it takes one (T740). The apply route refuses a Draft and a Void
 * with these words, and the Apply credit window says them before anybody presses
 * Apply. A Paid bill is refused by its settled balance, so its sentence is the
 * window's alone.
 */
export function billCreditStatusSentence(status: string): string | null {
  if (status === 'Draft') return 'This bill is Draft, and a draft bill takes no credit until it is submitted and posted.';
  if (status === 'Void') return 'This bill is Void, and a void bill takes no credit.';
  if (status === 'Paid') return 'This bill is Paid and owes nothing to apply a credit against.';
  return null;
}

/**
 * The refusals, in the order a person meets them. 🔨 Partial amounts are
 * allowed and the remainder stays available, so the only refusals are a credit
 * that cannot be applied at all, the wrong vendor, and an amount past either
 * side's room.
 */
export function creditApplicationProblem(facts: CreditApplicationFacts): string | null {
  if (facts.creditStatus === 'Draft') return 'this credit is still a draft; it applies once it is approved';
  if (facts.creditStatus === 'Void') return 'this credit was voided and applies to nothing';
  // 🔒 A bill not ready for credit is refused in a sentence naming its status (T740), the words the window shows before anybody presses Apply.
  if (facts.billStatus === 'Draft' || facts.billStatus === 'Void') return billCreditStatusSentence(facts.billStatus);
  const mismatch = vendorCreditBillProblem(facts.creditVendorCompanyId, facts.billVendorCompanyId);
  if (mismatch) return mismatch;
  if (!Number.isInteger(facts.amountCents) || facts.amountCents <= 0) return 'an application carries an amount above zero, in whole cents';
  const remaining = remainingCreditCents(facts);
  if (remaining <= 0) return 'this credit is fully applied and has nothing left to give';
  if (facts.amountCents > remaining) return `only ${remaining} cent(s) of this credit are left to apply`;
  if (facts.billOpenBalanceCents <= 0) return 'this bill is settled and owes nothing to apply a credit against';
  if (facts.amountCents > facts.billOpenBalanceCents) return `this bill owes ${facts.billOpenBalanceCents} cent(s); a credit never over-applies`;
  return null;
}

/** The most a person can apply in one act: whichever of the two sides has less room. */
export function maximumApplicableCents(facts: Pick<CreditApplicationFacts, 'creditAmountCents' | 'creditAppliedCents' | 'billOpenBalanceCents'>): number {
  return Math.max(0, Math.min(remainingCreditCents(facts), facts.billOpenBalanceCents));
}

/**
 * 🔨 The status ladder is read off the applied sum and never written to the
 * column: Open while nothing is applied, PartiallyApplied while some is, and
 * Applied once the sum reaches the credit's amount. The same function serves
 * before and after an application, which is what stops the two disagreeing.
 */
export function creditStatusAfterApplying(facts: { creditAmountCents: number; creditAppliedCents: number; applyingCents: number; stored: string }): string {
  return vendorCreditStatus({ amountCents: facts.creditAmountCents, appliedCents: facts.creditAppliedCents + facts.applyingCents, stored: facts.stored });
}

/** 🔨 Nothing applies a credit on its own. The answer is written down so a later automatic proposer finds it. */
export function automaticApplicationAllowed(): false {
  return false;
}
